import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { StatsPeriodSelection } from '@/app/pages/admin-options/settings/molecules/admin-stats-aggregate/admin-stats-aggregate.types';
import { formatStatsPeriodLabel, getYesterdayUtc } from '@/app/pages/admin-options/settings/molecules/admin-stats-period/admin-stats-period.helpers';
import { AdminStatsPeriodFilter } from '@/app/pages/admin-options/settings/molecules/admin-stats-period-filter/admin-stats-period-filter';
import { AdminGoogleAnalyticsService } from '../../../services/admin-google-analytics.service';
import { accountModeLabel, readGaErrorMessage } from '../account-mode.helpers';
import { GaAccountModeReport, GaAccountModeRow } from '../account-mode.types';

type AccountModeSortField = 'accountMode' | 'activeUsers' | 'eventCount';
type AccountModeSortOrder = 'ASC' | 'DESC';

@Component({
  selector: 'admin-google-analytics-list',
  templateUrl: './account-mode-list.html',
  styleUrl: './account-mode-list.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AdminStatsPeriodFilter, LoadingInlineComponent],
})
export class AccountModeList implements OnDestroy {
  report: GaAccountModeReport | null = null;
  rows: GaAccountModeRow[] = [];
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  error: string | null = null;
  rangeLabel = '';
  sortField: AccountModeSortField = 'activeUsers';
  sortOrder: AccountModeSortOrder = 'DESC';

  readonly accountModeLabel = accountModeLabel;
  readonly latestSnapshotDate = getYesterdayUtc();

  private subscriptionList = new Subscription();
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly gaService = inject(AdminGoogleAnalyticsService);

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onPeriodChange(selection: StatsPeriodSelection): void {
    this.loadRows(selection.startDate, selection.endDate);
  }

  isSortedBy(field: AccountModeSortField): boolean {
    return this.sortField === field;
  }

  getSortButtonClass(field: AccountModeSortField): string {
    const active = this.isSortedBy(field);
    if (active) {
      return 'inline-flex items-center gap-1 rounded text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-400';
    }
    return 'inline-flex items-center gap-1 rounded text-gray-700 hover:text-indigo-700 dark:text-zinc-300 dark:hover:text-indigo-400';
  }

  toggleSort(field: AccountModeSortField): void {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortField = field;
      this.sortOrder = field === 'accountMode' ? 'ASC' : 'DESC';
    }
    this.rows = this.sortRows(this.rows);
    this.refreshView();
  }

  get isLoading(): boolean {
    return this.loadingState === 'loading';
  }

  private loadRows(startDate: string, endDate: string): void {
    this.subscriptionList.unsubscribe();
    this.subscriptionList = new Subscription();
    this.loadingState = 'loading';
    this.error = null;
    this.refreshView();

    this.subscriptionList.add(
      this.gaService.getAccountMode(startDate, endDate).subscribe({
        next: (report) => {
          this.report = report;
          this.rangeLabel = formatStatsPeriodLabel(report.dateRange.startDate, report.dateRange.endDate);
          this.rows = this.sortRows(report.rows);
          this.loadingState = 'loaded';
          this.refreshView();
        },
        error: (err: unknown) => {
          this.report = null;
          this.rows = [];
          this.loadingState = 'error';
          this.error = readGaErrorMessage(err, 'Unable to load Google Analytics list.');
          this.refreshView();
        },
      }),
    );
  }

  private sortRows(rows: GaAccountModeRow[]): GaAccountModeRow[] {
    const direction = this.sortOrder === 'ASC' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (this.sortField === 'accountMode') {
        return a.accountMode.localeCompare(b.accountMode) * direction;
      }
      return (a[this.sortField] - b[this.sortField]) * direction;
    });
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }
}
